'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    // Parse headings from content
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headingElements = doc.querySelectorAll('h2, h3');
    
    const extractedHeadings: Heading[] = [];
    
    headingElements.forEach((heading, index) => {
      const text = heading.textContent || '';
      const id = `heading-${index}`;
      const level = parseInt(heading.tagName.charAt(1));
      
      extractedHeadings.push({ id, text, level });
    });
    
    setHeadings(extractedHeadings);
  }, [content]);

  useEffect(() => {
    // Add IDs to actual headings in the DOM
    const contentElement = document.querySelector('.prose');
    if (!contentElement) return;
    
    const headingElements = contentElement.querySelectorAll('h2, h3');
    headingElements.forEach((heading, index) => {
      heading.id = `heading-${index}`;
    });

    // Intersection Observer for active heading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0% -70% 0%'
      }
    );

    headingElements.forEach((heading) => {
      observer.observe(heading);
    });

    return () => {
      headingElements.forEach((heading) => {
        observer.unobserve(heading);
      });
    };
  }, [content]);

  if (headings.length === 0) {
    return null;
  }

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="mb-8 p-6 bg-gray-50 rounded-lg">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Table of Contents
      </h2>
      <ul className="space-y-2">
        {headings.map((heading) => (
          <li
            key={heading.id}
            className={cn(
              'transition-colors',
              heading.level === 3 && 'ml-4'
            )}
          >
            <button
              onClick={() => scrollToHeading(heading.id)}
              className={cn(
                'text-left w-full text-sm hover:text-primary transition-colors',
                activeId === heading.id
                  ? 'text-primary font-medium'
                  : 'text-gray-600'
              )}
            >
              {heading.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}